import { Controller } from "@hotwired/stimulus"
import db from "../chat_db"
import { getCachedChats, getCachedMessages, getOutboxMessages } from "../chat_offline_resources"

// Displays storage usage breakdown from navigator.storage.estimate()
// and counts from IndexedDB. Provides a "Clear Cache" action.
export default class extends Controller {
  static targets = [
    "usedMb", "quotaMb", "usageBar", "usagePct",
    "chatCount", "messageCount", "outboxCount",
    "clearButton"
  ]

  async connect() {
    await this._loadStorageEstimate()
    await this._loadIdbCounts()
  }

  async clearCache() {
    if (!confirm("Clear all offline chats and messages? Your account data will not be affected.")) return
    if (this.hasClearButtonTarget) this.clearButtonTarget.disabled = true

    try {
      await db.cached_chats.clear()
      await db.cached_messages.clear()
      await db.cached_participants.clear()

      // Clear page/service worker caches
      if ("caches" in window) {
        const keys = await caches.keys()
        await Promise.all(keys.map(k => caches.delete(k)))
      }

      await this._loadStorageEstimate()
      await this._loadIdbCounts()
    } finally {
      if (this.hasClearButtonTarget) this.clearButtonTarget.disabled = false
    }
  }

  // -- private --

  async _loadStorageEstimate() {
    if (!navigator.storage?.estimate) return
    try {
      const estimate = await navigator.storage.estimate()
      const usage = estimate.usage || 0
      const quota = estimate.quota || 1
      const pct   = Math.min(Math.round((usage / quota) * 100), 100)

      if (this.hasUsedMbTarget)  this.usedMbTarget.textContent  = `${(usage / 1024 / 1024).toFixed(1)} MB`
      if (this.hasQuotaMbTarget) this.quotaMbTarget.textContent = `${(quota / 1024 / 1024 / 1024).toFixed(1)} GB`
      if (this.hasUsagePctTarget) this.usagePctTarget.textContent = `${pct}%`
      if (this.hasUsageBarTarget)  this.usageBarTarget.style.width = `${pct}%`
    } catch {
      if (this.hasUsedMbTarget) this.usedMbTarget.textContent = "Unavailable"
    }
  }

  async _loadIdbCounts() {
    try {
      const chats = await getCachedChats()
      if (this.hasChatCountTarget) this.chatCountTarget.textContent = chats.length

      let msgTotal = 0
      for (const chat of chats) {
        const msgs = await getCachedMessages(chat.id)
        msgTotal += msgs.length
      }
      if (this.hasMessageCountTarget) this.messageCountTarget.textContent = msgTotal

      const outbox = await getOutboxMessages()
      if (this.hasOutboxCountTarget) this.outboxCountTarget.textContent = outbox.length
    } catch {
      // IDB not available
    }
  }
}
